import dayjs from 'dayjs'
import _ from 'lodash'
import type React from 'react'
import { type RefObject, useCallback } from 'react'
import { browserName } from 'react-device-detect'
// @ts-ignore
import saveSvgAsPng from 'save-svg-as-png'
import {
  type ChartPdfExtras,
  chartLegendContent,
  chartSubtitleContent,
  readChartPdfExtras
} from './chartLegendPdf'
import { loadImageAsDataUrl } from './images'
import PdfExport from './PdfExport'
import { Header, LogListTable, PageNumbers, StoolTable } from './pdfContent'
import { tableStyles } from './styles'

type LogPictures = Record<string, unknown>

/**
 * Loads every photo across the logs through one pool. Browsers open at most six
 * connections to a host, and S3 serves every photo from the same one, so more
 * would only queue. Fetching per batch of logs instead made each round wait for
 * its slowest photo, which turned a six-week PDF into minutes of waiting.
 */
const withPictureDataUrls = async (
  logs: LogPictures[]
): Promise<LogPictures[]> => {
  const updatedLogs = logs.map((log) => ({ ...log }))
  const photos = updatedLogs.flatMap((log) =>
    [1, 2, 3]
      .map((i) => `picture${i}`)
      .filter((key) => typeof log[key] === 'string')
      .map((key) => ({ log, key, url: log[key] as string }))
  )

  let next = 0
  const loadNext = async (): Promise<void> => {
    while (next < photos.length) {
      const { log, key, url } = photos[next++]
      try {
        log[`${key}Base64`] = await loadImageAsDataUrl(url)
      } catch (err) {
        console.error(`Failed to load image: ${url}`, err)
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(PHOTO_DOWNLOADS_AT_ONCE, photos.length) },
      loadNext
    )
  )
  return updatedLogs
}

interface ChartPdfExportProps {
  /** Wraps the chart to export, so other charts on the page are never captured. Without it, no chart is exported. */
  chartContainerRef?: RefObject<HTMLElement | null>
  resident: any
  timeFormat: string
  title: string
  groupBy: string
  logs: any[]
  type: string
  dataUrl: string | null
  query: any
  /** Notice shown under the title when the PDF holds fewer logs than the range. */
  partialNotice?: string
}

const totalFromResponse = (response: unknown): number | undefined => {
  const count = (response as { count?: unknown } | undefined)?.count
  return typeof count === 'number' ? count : undefined
}

const ChartPdfExport: React.FC<ChartPdfExportProps> = ({
  chartContainerRef,
  resident,
  timeFormat,
  title,
  groupBy,
  logs,
  type,
  dataUrl,
  query,
  partialNotice
}) => {
  const SVGtoPNG = (svg: string): Promise<string> => {
    return new Promise((resolve) => {
      saveSvgAsPng.svgAsPngUri(svg, { scale: 3 }, (uri: string) => {
        resolve(uri)
      })
    })
  }

  const getDocDefinition = (
    image: string | null,
    extras: ChartPdfExtras,
    getBody: any,
    fetchedLogs: any[],
    notice: string | null
  ) => {
    const filteredLogs = (fetchedLogs || logs).map((log: any) =>
      log.deleted
        ? { ...log, description: null, title: 'Log Removed' }
        : { ...log }
    )

    const _logs = _.sortBy(filteredLogs, (d) => -dayjs(d.timeLogged))

    let pages
    if (groupBy === 'date') {
      pages = _.chain(_logs)
        .groupBy(({ timeLogged }) => dayjs(timeLogged).format('DD MMM YYYY'))
        .map((group, date) => ({
          heading: date,
          logs: group,
          timeLogged: _.get(group, '0.timeLogged')
        }))
        .orderBy(({ timeLogged }) => -dayjs(timeLogged))
        .value()
    } else {
      pages = [{ logs: _.orderBy(_logs, (d) => -dayjs(d.timeLogged)) }]
    }

    return {
      content: pages.map((row, index) => {
        const legend = image ? chartLegendContent(extras.legend) : null
        const chartImage = image
          ? {
              width: 500,
              margin: [0, 10, 0, legend ? 6 : 40],
              alignment: 'center',
              image
            }
          : null
        const rowTitle = `${resident.name.first} ${resident.name.last} - ${title}`
        return [
          // @ts-ignore
          Header({ title: rowTitle, date: row.heading }),
          index === 0 && notice ? { text: notice, style: 'notice' } : null,
          image ? chartSubtitleContent(extras.subtitle) : null,
          chartImage,
          legend,
          getBody(row, timeFormat),
          index + 1 !== pages.length ? { pageBreak: 'after', text: '' } : null
        ].filter(Boolean)
      }),
      footer: PageNumbers,
      styles: tableStyles
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const prepareDocDefinition = useCallback(
    async (fetchedLogs: any[], response?: unknown) => {
      let image: string | null = null
      const chartContainer = chartContainerRef?.current
      const chartElement = chartContainer?.querySelector(
        '[data-slot="chart"] .recharts-surface'
      )

      try {
        if (
          browserName &&
          browserName.match(/Chrome|Chromium|Edge|Firefox/) &&
          chartElement
        ) {
          // The chart's font is inherited from page CSS, which the PNG render doesn't see.
          const fontFamily = chartElement.getAttribute('font-family')
          chartElement.setAttribute(
            'font-family',
            getComputedStyle(chartElement).fontFamily
          )
          try {
            image = await SVGtoPNG(chartElement as unknown as string)
          } finally {
            if (fontFamily === null) {
              chartElement.removeAttribute('font-family')
            } else {
              chartElement.setAttribute('font-family', fontFamily)
            }
          }
        }
      } catch (e) {
        console.error(`SVG to PNG error:`, e)
      }
      // The legend and subtitle are HTML outside the captured SVG.
      const extras = readChartPdfExtras(
        chartElement?.closest('[data-slot="chart"]') ?? null,
        chartContainer?.querySelector('[data-chart-subtitle]') ?? null
      )

      const logsWithBase64 = await withPictureDataUrls(fetchedLogs || logs)

      const total = totalFromResponse(response)
      const notice =
        partialNotice &&
        total !== undefined &&
        total > (fetchedLogs || logs).length
          ? partialNotice
          : null

      return type === 'stool'
        ? getDocDefinition(image, extras, StoolTable, logsWithBase64, notice)
        : getDocDefinition(image, extras, LogListTable, logsWithBase64, notice)
    },
    [logs, type, groupBy, resident, title, timeFormat, partialNotice]
  )

  const getUrl = (url: string, query: any = {}) => {
    const { to, from } = query
    let result = `${url}?limit=10000`
    if (from) result += `&from=${from}`
    if (to) result += `&to=${to}`
    return result
  }

  return (
    <PdfExport
      getDocDefinition={prepareDocDefinition}
      // @ts-ignore
      dataUrl={dataUrl && getUrl(dataUrl, query)}
      pdfTitle={title}
      style={{ float: 'right' }}
    />
  )
}

const PHOTO_DOWNLOADS_AT_ONCE = 6

export default ChartPdfExport
